import { SystemStyleObject } from "@chakra-ui/styled-system";
import { GenericTableColumnHeaderThemeEnum } from "gen/model/genericTableColumnHeaderThemeEnum";
// eslint-disable-next-line no-restricted-imports
import { COLUMN_ID_CONTROLS_COLUMN } from "shared/components/table/data/internal/DataTableConstants";
import {
  ActionCellClass,
  cellBackgroundInheritClass,
  ClickableRowClass,
  maybernCellErrorClass,
} from "shared/components/table/maybern/MaybernTableConstants";
import { shadows } from "theme/shadows";

export const MaybernTableAgTheme: SystemStyleObject = {
  ".ag-theme-maybern": {
    "--ag-icon-font-family": "agGridAlpine",
    "--ag-data-color": "var(--chakra-colors-gray-600)",
    "--ag-font-family": "var(--chakra-fonts-body)",
    "--ag-border-color": "var(--chakra-colors-gray-200)",
    "--ag-grid-size": "var(--chakra-space-1)",
    "--ag-foreground-color": "var(--chakra-colors-chakra-body-text)",
    "--ag-range-selection-background-color": "var(--chakra-colors-primary-25)",
    "--ag-range-selection-border-color": "var(--chakra-colors-primary-400)",
    "--ag-header-height": "55px", // 56px - 1px border
    "--ag-group-header-height": "35px",
    "--ag-row-height": "39px",
    "--ag-row-group-indent-size": "14px",
    "--ag-row-hover-color": "transparent",
    "--ag-cell-horizontal-padding": "16px",
    "--ag-header-foreground-color": "var(--chakra-colors-gray-500)",
    "--ag-font-size": "14px",
    "--ag-widget-container-horizontal-padding": "12px",
    "--ag-list-item-height": "32px",
    "--ag-disabled-foreground-color": "var(--chakra-colors-gray-400)",
    "--ag-toggle-button-on-background-color":
      "var(--chakra-colors-primary-600)",
    "--ag-toggle-button-on-border-color": "var(--chakra-colors-primary-600)",
    "--ag-toggle-button-height": "18px",
    "--ag-toggle-button-width": "28px",
    "--ag-input-focus-border-color": "var(--chakra-colors-primary-500)",
    ".is-disabled div.mb-dt-cell-content": {
      opacity: 0.5,
      cursor: "not-allowed",
    },
    ".ag-side-bar": {
      ".ag-side-buttons": {
        width: "32px",
        paddingTop: 0,
      },
      ".ag-side-button-button": {
        borderTopWidth: 0,
      },
      ".ag-side-button": {
        bg: "gray.50",
        "&.ag-selected": {
          bg: "white",
        },
      },
      ".ag-drag-handle": {
        "--ag-icon-size": "16px",
      },
      ".ag-text-field-input": {
        padding: "8px",
        borderRadius: "6px",
      },
      ".ag-column-drop-vertical": {
        ".ag-column-drop-vertical-list": {
          pl: "12px",
          py: "2px",
        },
        ".ag-column-drop-vertical-title-bar": {
          bg: "var(--chakra-colors-gray-25)",
          p: "4px",
          borderBottom: "1px solid var(--chakra-colors-gray-100)",
        },
        ".ag-column-drop-vertical-cell": {
          minHeight: "32px",
          my: "0px",
        },
        ".ag-column-drop-empty-message": {
          p: "8px",
          textAlign: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        ".ag-column-drop-cell-button .ag-icon::before": {
          color: "var(--chakra-colors-gray-500)",
          fontSize: "16px",
        },
        ".ag-column-drop-cell": {
          pl: 0,
        },
        ".ag-column-drop-cell-drag-handle": {
          ml: 0,
        },
      },
      ".ag-filter-toolpanel-search": {
        borderBottomWidth: "1px",
      },
      ".ag-filter-apply-panel": {
        borderTopWidth: 0,
      },
      ".ag-filter-toolpanel-group": {
        ".ag-filter-apply-panel": {
          display: "none",
        },
        "&.ag-has-filter": {
          ".ag-filter-apply-panel": {
            display: "flex",
          },
        },
      },
      ".ag-filter-toolpanel-group-level-0": {
        borderTopWidth: 0,
      },
      ".ag-filter-toolpanel-group-level-0-header": {
        bg: "gray.25",
        height: "unset !important",
        py: "8px",
        px: "12px",
        borderBottomWidth: "1px",
      },
      ".ag-filter-toolpanel-instance-filter": {
        borderTopWidth: 0,
        marginTop: 0,
      },
      ".ag-filter-toolpanel-group-item": {
        marginTop: 0,
      },
      ".ag-button": {
        "--ag-input-focus-box-shadow": shadows.outline,
      },
    },
    "&:not(.ag-dnd-ghost)": {
      width: "100%",
    },
    "&.mb-datatable-not-ready": {
      // do not show table before calling autoSizeAllColumns() to avoid flickering
      visibility: "hidden",
    },
    "&.mb-tablebox-body": {
      "&:not(:has(.empty-state))": {
        visibility: "hidden",
        "&.mb-datatable-ready": {
          visibility: "visible",
        },
        ".mb-tablebox-fullscreen &": {
          flex: 1,
        },
      },
    },
    "&.mb-datatable-size-comfortable": {
      "--ag-row-height": "56px",
    },
    "&": {
      lineHeight: "var(--chakra-lineHeights-base)",
      fontVariantNumeric: "lining-nums tabular-nums",
    },
    ".ag-root-wrapper": {
      border: "none",
    },
    // Extend row lines to fill the entire table
    ".ag-center-cols-container, .ag-floating-bottom-container": {
      minWidth: "100%",
    },
    ".ag-cell-wrapper": {
      height: "100%",
    },
    ".ag-cell-focus": {
      bg: "primary.50",
      zIndex: "2 !important",
    },
    ".ag-center-cols-container, .ag-pinned-right-cols-container": {
      marginRight: "0 !important",
    },
    ".ag-pinned-right-header, .ag-pinned-right-cols-container .ag-cell.ag-cell-first-right-pinned:not(.ag-cell-range-left):not(.ag-cell-range-single-cell)":
      {
        borderLeftColor: "transparent",
        "&.ag-cell-focus": {
          bg: "none",
          borderColor: "transparent",
        },
      },
    ".ag-cell[col-id='mb-col-id-actions'].ag-cell-focus": {
      bg: "none !important",
      borderColor: "transparent",
    },
    [`.ag-cell[aria-colindex="1"]:not([col-id="${COLUMN_ID_CONTROLS_COLUMN}"])`]:
      {
        paddingInlineStart: "23px", // 24px - 1px border

        "&.mb-cell-is-header, .mb-row-is-total &": {
          fontWeight: 500,
        },
      },
    ".ag-header-cell": {
      whiteSpace: "normal",
      lineHeight: "var(--chakra-lineHeights-base)",
      overflow: "hidden",
      ".ag-header-cell-text, .mb-header-cell-comp-title": {
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
      },
      "*, *::before, &::after": {
        wordWrap: "normal",
      },
    },
    '.ag-header-cell[aria-colindex="1"]': {
      paddingLeft: "24px",
    },
    ".ag-row.ag-row-hover.mb-row-is-hoverable": {
      "--ag-row-hover-color": "var(--chakra-colors-gray-50)",
    },
    // NOTE: "The grid cannot depend on using CSS :hover selector as this will
    // not highlight the entire row if Columns are pinned."
    // https://www.ag-grid.com/javascript-data-grid/row-styles/
    [`.ag-row.ag-row-hover.${ClickableRowClass}`]: {
      // .mb-cell-actions-cell must be clickable
      [`.ag-cell:not(.${ActionCellClass})`]: {
        // Don't select cells when row is clickable but
        // allow expanding/collapsing row groups
        // FIXME: This is not a good solution.
        //  - Breaks drag-to-select (mostly but not always)
        //  - Doesn't prevent cell selection when clicking the expand/collapse button
        pointerEvents: "none",
        ".ag-group-contracted, .ag-group-expanded": {
          pointerEvents: "auto",
        },
        background: "transparent",
      },
      "*:has(:hover):not(:has(.mb-datatable-row-actions:hover)) &": {
        "--ag-row-hover-color": "var(--chakra-colors-gray-50)",
      },
      "*:has(:active):not(:has(.mb-datatable-row-actions:active)) &": {
        "--ag-row-hover-color": "var(--chakra-colors-gray-100)",
      },
      cursor: "pointer",
      zIndex: 1,
      _before: {
        // Don't show x-axis shadows because a single row may consist of
        // multiple elements for pinned columns as explained above.
        clipPath: "inset(-8px 0 -8px 0)",
        shadow: "tableHover",
      },
    },
    [".ag-row-group-expanded .ag-cell:not(.mb-cell-is-header) .mb-dt-cell-content, " +
    ".mb-dt-cell-content.mb-dt-cell-varies"]: {
      display: "flex",
      alignItems: "center",
      opacity: 0.5,
    },
    ".ag-row-selected": {
      _before: {
        bg: "none",
      },
    },
    "&.mb-datatable-selection-without-checkboxes .ag-row-selected": {
      "--ag-background-color": "var(--chakra-colors-gray-100)",
      bg: "var(--ag-background-color)",
    },
    ".ag-row.mb-row-is-total .ag-cell": {
      fontWeight: 500,
    },
    [[
      ".ag-header-group-cell",
      ".ag-header-cell.mb-cell-last-in-group",
      ".ag-header-cell.mb-cell-last-in-group",
      ".ag-header-cell.mb-column-has-form-field",
      ".ag-header-cell.mb-header-last-in-group",
      ".ag-cell.mb-cell-last-in-group",
      ".ag-cell.mb-column-has-form-field",
    ].join(", ")]: {
      [`&:not(.ag-cell-focus):not(.${maybernCellErrorClass}):not(.ag-cell-range-selected), ` +
      "&.ag-cell-focus:not(:focus-within)"]: {
        borderRightWidth: "1px",
        borderRightColor: "var(--ag-border-color)",
      },
    },
    ".ag-body-viewport:not(.ag-has-focus)": {
      ".ag-cell.ag-cell-range-selected": {
        "--ag-range-selection-border-color": "var(--chakra-colors-primary-100)",
      },
    },
    ".ag-header-group-cell": {
      borderRightWidth: "1px",
      borderRightColor: "var(--ag-border-color)",
      paddingLeft: 0,
      paddingRight: 0,
    },
    ".ag-ltr .ag-header-group-cell-label.ag-sticky-label": {
      left: 0,
      px: "var(--ag-cell-horizontal-padding)",
    },
    // border between pinned+grouped column headers
    ".ag-pinned-left-header .ag-header-cell.ag-header-span-height": {
      borderRightWidth: "1px",
      borderRightColor: "var(--ag-border-color)",
    },
    ".mb-header-parent-is-placeholder, .ag-header-cell.mb-header-parent-no-title":
      {
        borderTop: "none !important",
      },
    ".mb-tablebox:not(.mb-tablebox-fullscreen):not(.mb-tablebox-fillheight) &":
      {
        [".ag-row.ag-row-last, .mb-row-is-last-pinned-bottom, " +
        ".ag-row.ag-row-last:not(.ag-row-first), " +
        ".ag-floating-bottom-container .ag-row.ag-row-pinned.mb-row-is-last-pinned-bottom, " +
        ".ag-pinned-left-floating-bottom .ag-row.ag-row-pinned.mb-row-is-last-pinned-bottom, " +
        ".ag-pinned-right-floating-bottom .ag-row.ag-row-pinned"]: {
          borderBottomWidth: 0,
        },
      },
    ".ag-cell.mb-cell-is-skipped": {
      display: "none",
    },
    ".ag-cell.mb-cell-computed": {
      "--ag-background-color": "var(--chakra-colors-gray-50)",
      bg: "var(--ag-background-color)",
    },
    ".ag-cell.mb-cell-is-row-spanned": {
      // Cover up borders of cells that this cell spans over
      // leave one pixel transparent at the bottom to avoid covering up
      // the row border
      background: "linear-gradient(0deg, transparent 1px, white 1px)",
      backgroundRepeat: "no-repeat",
    },
    ".mb-cell-is-row-spanned": {
      ["&:not(.mb-cell-is-row-spanned-to-bottom)" +
      ":not(.ag-cell-focus)" +
      `:not(.${maybernCellErrorClass})` +
      ":not(.ag-cell-range-selected), " +
      "&.ag-cell-focus" +
      ":not(:focus-within)" +
      ":not(.mb-cell-is-row-spanned-to-bottom)"]: {
        borderBottomWidth: "1px",
        borderBottomColor: "var(--ag-border-color)",
      },
    },
    ".ag-cell.ag-cell-focus:not(.ag-cell-range-selected).mb-cell-is-left-actions":
      {
        borderTopColor: "transparent",
        borderRightColor: "var(--ag-border-color)",
        borderBottomColor: "transparent",
        borderLeftColor: "transparent",
        bg: "transparent !important",
      },
    ".mb-dt-cell-content": {
      width: "100%",
      lineHeight: "normal",
      display: "inline-flex",
      flexDirection: "row",
      alignItems: "center",
      gap: "8px",
    },
    ".mb-dt-cell-content:has(select)": {
      flex: 1,
    },
    "button .mb-dt-cell-content": {
      justifyContent: "center",
    },
    ".ag-header-row:not(:first-of-type) .ag-header-group-cell:not(.ag-header-span-height)":
      {
        borderTop: "var(--ag-borders-critical) var(--ag-border-color)",
      },
    ".ag-header-row-column-group": {
      ".mb-header-cell-comp": {
        justifyContent: "center",
        alignItems: "center",
      },
      ".ag-header-group-cell.mb-header-has-title, .ag-header-group-cell:has(.ag-header-group-text:not(:empty))":
        {
          justifyContent: "center",
          textColor: "var(--chakra-colors-gray-600)",
          bg: "var(--chakra-colors-primary-25)",
          "--ag-header-cell-hover-background-color":
            "var(--chakra-colors-primary-25)",
          [`&.${GenericTableColumnHeaderThemeEnum.eggplant}`]: {
            bg: "var(--chakra-colors-eggplant-25)",
            "--ag-header-cell-hover-background-color":
              "var(--chakra-colors-eggplant-25)",
          },
        },
      ".mb-header-row-not-last": {
        "&.ag-header-group-cell.mb-header-has-title": {
          textColor: "var(--chakra-colors-gray-800)",
          bg: "var(--chakra-colors-primary-50)",
          _hover: {
            bg: "var(--chakra-colors-primary-50)",
          },
        },
      },
    },
    '.ag-header-cell:has(.mb-header-cell-comp):not([aria-colindex="1"])': {
      paddingInline: 0,
    },
    ".ag-cell-label-container": {
      py: "0px",
    },
    ".ag-header-cell-label": {
      py: "8px",
      alignItems: "flex-end",
    },
    ".ag-cell.mb-cell-row-header": {
      fontWeight: 500,
    },
    ".ag-header-cell, .ag-cell": {
      "&.mb-cell-highlighted": {
        backgroundColor: "primary.50",
      },
    },
    ".ag-header": {
      _hover: {
        "& .ag-header-cell-resize": {
          bg: "gray.100",
        },
      },
    },
    ".ag-cell": {
      display: "flex",
      overflow: "hidden",
    },
    [[
      ".mb-cell-value-type-money",
      ".mb-cell-value-type-decimal",
      ".mb-cell-value-type-percent",
      ".mb-header-value-type-money",
      ".mb-header-value-type-decimal",
      ".mb-header-value-type-percent",
      ".mb-justify-right",
    ].join(", ")]: {
      "&.ag-cell, .mb-header-cell-comp, .mb-dt-cell-content": {
        justifyContent: "flex-end",
        textAlign: "right",
      },
    },
    ".ag-cell.mb-cell-value-type-boolean": {
      justifyContent: "center",
      ".mb-dt-cell-content": {
        justifyContent: "center",
      },
    },
    ".mb-cell-value-type-money_stepper": {
      padding: 0,
      border: "none",
    },
    ".ag-header-cell.mb-header-value-type-boolean": {
      textAlign: "center",
      ".mb-header-cell-comp-wrapper, .mb-header-cell-comp-header-wrapper": {
        w: "100%",
      },
    },
    ".mb-header-cell-comp": {
      width: "100%",
      height: "100%",
      display: "inline-flex",
      justifyContent: "space-between",
      alignItems: "flex-end",
      gap: "8px",
      paddingInline: "16px",
      py: "8px",
      "&[tabindex]": {
        cursor: "pointer",
        _hover: {
          bg: "gray.50",
        },
        _active: {
          bg: "gray.100",
        },
      },
    },
    ".ag-header-cell.mb-cell-group-column-header": {
      pl: "16px",
      ".ag-header-cell-comp-wrapper": {
        alignItems: "flex-end",
        w: "100%",
        h: "100%",
      },
    },
    ".ag-header-cell.ag-header-cell-auto-height": {
      alignItems: "flex-end",
    },
    '.ag-header-cell[aria-colindex="1"] .mb-header-cell-comp': {
      paddingInline: 0,
    },
    ".mb-header-cell-comp-title": {
      "&[data-currency-symbol]": {
        _after: {
          content: '"\u00a0" attr(data-currency-symbol)',
          color: "gray.400",
        },
      },
    },
    ".ag-header-cell.mb-header-value-type-money": {
      ".ag-header-cell-label": {
        justifyContent: "flex-end",
        ".ag-header-cell-text": {
          textAlign: "right",
        },
      },
      ".ag-header-cell-text": {
        _after: {
          content: '"\u00a0" "$"',
          display: "inline-block",
          color: "gray.400",
        },
      },
    },
    ".mb-header-cell-comp-subtitle": {
      textStyle: "small",
      color: "gray.500",
    },
    ".ag-header-cell, & .ag-header-group-cell": {
      fontWeight: 500,
      fontSize: "12px",
    },
    ".ag-header-group-cell-label": {
      justifyContent: "center",
    },
    ".ag-group-expanded, .ag-group-contracted": {
      marginRight: "0px",
    },
    ".mb-group-child-count": {
      paddingLeft: "8px",
      textColor: "var(--chakra-colors-gray-400)",
    },
    ".ag-selection-checkbox": {
      marginRight: "12px",
      "&.ag-invisible": {
        display: "none",
      },
    },
    ".ag-selection-checkbox .ag-checkbox": {
      "--ag-icon-size": "20px",
      "--ag-checkbox-radius": "6px",
    },
    ".ag-checkbox": {
      "--ag-icon-size": "16px",
      "--ag-checkbox-radius": "4px",
      ".ag-checkbox-input-wrapper": {
        width: "var(--ag-icon-size)",
        height: "var(--ag-icon-size)",
        borderWidth: "1px",
        borderColor: "var(--chakra-colors-gray-300)",
        borderStyle: "solid",
        borderRadius: "var(--ag-checkbox-radius)",
        _after: {
          content: "''",
          mt: "-1px",
          ml: "-1px",
          w: "var(--ag-icon-size)",
          h: "var(--ag-icon-size)",
        },
        _hover: {
          bgColor: "var(--chakra-colors-primary-50)",
          borderColor: "var(--chakra-colors-primary-600)",
        },
        "&.ag-checked": {
          borderColor: "primary.600",
          bgColor: "primary.50",
          _after: {
            bgColor: "primary.600",
            maskRepeat: "no-repeat",
            maskPosition: "center",
            maskSize: "14px",
            maskImage: "url(/img/check.svg)",
          },
        },
      },
    },
    ".ag-row.mb-row-selection-single .ag-checkbox": {
      ".ag-checkbox-input-wrapper": {
        borderRadius: "var(--ag-icon-size)",
        "&.ag-checked": {
          _after: {
            maskImage: "url(/img/radio.svg)",
            maskSize: "var(--ag-icon-size)",
          },
        },
      },
    },
    ".ag-row:not(.ag-row-hover)": {
      ".ag-header-cell, .ag-cell": {
        // Improves appearance while columns are moving around (dragging/grouping/pinning)
        "&:not(.ag-cell-focus):not(.ag-cell-range-selected):not(.mb-cell-highlighted)":
          {
            background: "var(--ag-background-color)",
          },
      },
    },
    ".ag-icon.ag-icon-tree-closed, .ag-icon.ag-icon-tree-open": {
      color: "gray.500",
      borderRadius: "2px",
      marginLeft: "-5px",
      marginRight: "4px",
      width: "16px",
      height: "16px",
      alignItems: "center",
      justifyContent: "center",
      display: "flex",
      _before: {
        display: "inline-block",
        color: "transparent",
        backgroundImage: 'url("/img/small-arrow.svg")',
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        width: "16px",
        height: "16px",
        paddingLeft: "24px",
      },
      _hover: {
        bg: "gray.100",
        _before: {
          filter: "brightness(80%)",
        },
      },
      _active: {
        bg: "gray.200",
      },
    },
    // FIXME: This does not work because the DOM node is replaced
    //  transitionProperty: "transform",
    //  transitionDuration: "200ms",
    ".ag-icon.ag-icon-tree-closed": {
      _before: {
        transform: "rotate(0deg)",
      },
    },
    ".ag-icon.ag-icon-tree-open": {
      _before: {
        transform: "rotate(90deg)",
      },
    },
    '.ag-cell:has(input[type="text"], input[type="date"], input[role="combobox"], .react-select__control)':
      {
        px: 0,
        background: "var(--ag-background-color)",
      },
    ".ag-cell.mb-cell-dropdown": {
      px: 0,
    },
    ['.ag-cell:has(input[type="text"], input[type="date"])' +
    ':not(:has(input[role="combobox"]))' +
    ":not(:has(input[disabled])):not(:has(input[readonly]))"]: {
      overflow: "visible",
      _after: {
        content: '""',
        boxShadow: "0px 0px 6px 0px rgba(208, 209, 221, 0.50) inset",
        pointerEvents: "none",
        // position shadow over the border
        position: "absolute",
        top: "-1px",
        right: "-1px",
        bottom: "-1px",
        left: "-1px",
      },
    },
    [`.ag-cell:not(.${maybernCellErrorClass}):has(input[type="text"][readonly])`]:
      {
        "--ag-background-color": "var(--chakra-colors-gray-50)",
        backgroundColor: "var(--ag-background-color)",
        [`&.${cellBackgroundInheritClass}`]: {
          "--ag-background-color": "unset",
          backgroundColor: "unset",
        },
      },
    ".ag-cell:has(input):not(:has(.ag-selection-checkbox))": {
      justifyContent: "center",
    },
    [`.ag-cell.${maybernCellErrorClass}`]: {
      "--ag-border-color": "var(--chakra-colors-error-700)",
      "--ag-range-selection-border-color": "var(--chakra-colors-error-700)",
      "--ag-background-color": "var(--chakra-colors-error-25)",
      borderColor: "var(--chakra-colors-error-700)",
      bg: "var(--ag-background-color)",
      "*": {
        color: "var(--chakra-colors-error-700)",
        textDecorationColor: "var(--chakra-colors-error-700)",
      },
      "& a:hover, & a:hover *": {
        color: "var(--chakra-colors-error-800)",
        textDecorationColor: "var(--chakra-colors-error-800)",
      },
      "& a:active, & a:active *": {
        color: "var(--chakra-colors-error-900)",
        textDecorationColor: "var(--chakra-colors-error-900)",
      },
      "input::placeholder": {
        color: "error.400",
      },
    },
    ".ag-overlay": {
      // allow interacting with buttons in empty overlay
      pointerEvents: "all",
    },
    ".ag-layout-auto-height .ag-center-cols-viewport": {
      minHeight: "unset",
    },
    ".ag-root:not(:has(* > .ag-overlay-no-rows-wrapper > *))": {
      ".ag-body, .ag-layout-auto-height .ag-center-cols-viewport": {
        minHeight: "var(--ag-row-height)",
      },
    },
    ".ag-root:has(* > .ag-overlay-no-rows-wrapper > *) .ag-header": {
      // Hide header when empty overlay is shown (has children)
      display: "none",
    },
    ".ag-overlay:has(.ag-overlay-no-rows-wrapper)": {
      position: "initial",
    },
    ".ag-overlay-no-rows-wrapper.ag-layout-auto-height": {
      // Padding is handled by the overlay component
      paddingTop: 0,
    },
    ".ag-cell:has(.ag-checkbox-cell), .ag-checkbox-edit": {
      justifyContent: "center",
    },
    ".ag-header-cell.mb-header-cell-centered .ag-header-cell-label": {
      justifyContent: "center",
      textAlign: "center",
    },
    ".ag-cell.mb-cell-centered": {
      justifyContent: "center",
      textAlign: "center",
    },
    //#region filter popup
    ".ag-menu": {
      "--ag-tab-min-width": "320px",
      "--ag-widget-vertical-spacing": "10px",
      "--ag-popup-shadow": "var(--chakra-shadows-md)",
      "--ag-input-focus-border-color": "var(--chakra-colors-primary-500)",
      "--chakra-colors-chakra-placeholder-color":
        "var(--chakra-colors-gray-400)",
      borderRadius: "12px",
      ".ag-menu-header": {
        "--ag-icon-size": "18px",
        "--ag-icon-font-color": "var(--chakra-colors-gray-500)",
        borderBottomWidth: "1px",
        minHeight: "28px",
        alignItems: "center",
        justifyContent: "center",
      },
      ".ag-simple-filter-body-wrapper": {
        pt: "16px",
        pb: "6px",
        px: "16px",
      },
    },
    ".ag-popup-child:not(.ag-tooltip-custom)": {
      "--ag-row-hover-color": "var(--chakra-colors-primary-50)",
      "--ag-popup-shadow": "var(--chakra-shadows-md)",
    },
    ".ag-select-list": {
      borderRadius: "4px",
      borderWidth: 0,
    },
    ".ag-select-list-item": {
      px: "12px",
      py: "6px",
    },
    ".ag-input-field-input, .ag-ltr .ag-select .ag-picker-field-wrapper": {
      borderRadius: "4px",
      py: "6px",
      pl: "16px",
      pr: "10px",
      minHeight: "var(--ag-list-item-height)",
    },
    //#endregion
    // Full height column group dividers when table is fullscreen
    // This is the only time the table might have extra space below
    // the last row.
    ".mb-tablebox-fullscreen & .ag-pinned-left-cols-container": {
      height: "100% !important",
      borderRightWidth: 1,
      borderRightColor: "var(--ag-border-color)",
    },
    //#region Make sizing/scrolling work with autoHeight enabled
    ".ag-theme-maybern > div": {
      height: "100%",
    },
    ".ag-root-wrapper.ag-layout-auto-height": {
      maxHeight: "100%",
      height: "100%",
    },
    ".ag-root-wrapper-body.ag-layout-auto-height": {
      maxHeight: "100%",
    },
    ".ag-body-viewport.ag-layout-auto-height": {
      overflowY: "auto",
    },
    ".ag-root-wrapper-body.ag-layout-normal": {
      height: "unset !important",
    },
  },
  //#endregion
  //#region Move horizontal scrollbar below bottom pinned rows
  ".ag-center-cols-viewport": {
    scrollbarWidth: "none", // Firefox
  },
  ".ag-center-cols-viewport::-webkit-scrollbar": {
    display: "none", // Chrome
  },
  ".ag-body-horizontal-scroll.ag-apple-scrollbar.ag-scrollbar-invisible": {
    bottom: "0px !important",
  },
  ".ag-header-cell-comp-wrapper": {
    justifyContent: "center",
    whiteSpace: "normal",
  },
  //#endregion
};
